<template>
  <router-link :to="{ name: 'NoteDetail', params: { id: note.id } }" class="note-card">
    <div class="note-card__header">
      <span v-if="typeBadge" class="note-card__type">{{ typeBadge }}</span>
      <span v-if="note._shared" class="note-card__shared">
        {{ $t("notes.sharedBy", { name: note._ownerName || "—" }) }}
      </span>
      <span v-if="note.folder" class="note-card__folder" :title="note.folder">
        {{ note.folder }}
      </span>
    </div>
    <h3 class="note-card__title">
      <SfIcon
        v-if="titleIcon"
        :name="titleIcon"
        :size="16"
        :stroke-width="2.2"
        class="note-card__title-icon"
      />{{ titleText }}
    </h3>
    <p v-if="preview" class="note-card__preview">{{ preview }}</p>
    <div class="note-card__footer">
      <span class="note-card__date">{{ relativeDate }}</span>
      <span v-if="note.isLoading" class="note-card__badge">
        {{ $t("notes.generating") }}
      </span>
      <span v-else-if="note.deleted" class="note-card__badge note-card__badge--warn">
        {{ $t("notes.trashed") }}
      </span>
    </div>
  </router-link>
</template>

<script>
import { noteRowTitle, notePreviewUnderTitle } from "../utils/markdown";
import { formatRelative } from "../utils/dateFormat";
import SfIcon from "./SfIcon.vue";

// The phone's row symbols (`photo`, `cube.transparent`, `function`,
// `scribble.variable`) by their SfIcon names.
const TITLE_ICONS = { photo: "image", cube: "cube", function: "formula", scribble: "scribble" };

export default {
  name: "NoteCard",
  components: { SfIcon },
  props: {
    note: { type: Object, required: true },
  },
  computed: {
    rowTitle() {
      return noteRowTitle(this.note.content || "");
    },
    /** A note still being generated has no title yet; it is not a drawing. */
    isAwaitingContent() {
      return this.note.isLoading === true && !this.rowTitle.text;
    },
    titleText() {
      if (this.isAwaitingContent) return this.$t("notes.untitled");
      const { text, label } = this.rowTitle;
      return label ? this.$t(`notes.row.${label}`) : text;
    },
    titleIcon() {
      if (this.isAwaitingContent) return null;
      return TITLE_ICONS[this.rowTitle.symbol] || null;
    },
    preview() {
      return notePreviewUnderTitle(this.note.content || "", this.rowTitle);
    },
    relativeDate() {
      const locale = (this.$i18n && this.$i18n.locale) || "en";
      return formatRelative(this.note.timestamp, locale);
    },
    typeBadge() {
      if (!this.note.noteType || this.note.noteType === "CLASSIC") return "";
      // Use $te() to silence intlify "key not found" warnings when a note
      // carries a noteType the locale dictionaries don't know about — fall
      // back to a humanized version of the raw type instead.
      const key = "notes.type." + this.note.noteType;
      if (this.$te && this.$te(key)) return this.$t(key);
      const raw = String(this.note.noteType);
      return raw.charAt(0) + raw.slice(1).toLowerCase();
    },
  },
};
</script>

<style scoped>
.note-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 18px 16px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 16px;
  box-shadow: var(--app-shadow);
  text-decoration: none;
  color: var(--app-text);
  min-height: 180px;
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.note-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--app-text) 22%, transparent);
}

.note-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--app-text-muted);
  text-transform: uppercase;
}

.note-card__type {
  background: color-mix(in srgb, var(--app-text) 8%, transparent);
  padding: 2px 8px;
  border-radius: 999px;
}

.note-card__shared {
  background: color-mix(in srgb, var(--app-accent) 14%, transparent);
  color: var(--app-accent);
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: none;
  letter-spacing: 0;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-card__folder {
  background: color-mix(in srgb, var(--app-accent) 12%, transparent);
  color: var(--app-accent);
  padding: 2px 8px;
  border-radius: 999px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: none;
  letter-spacing: 0;
}

.note-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--app-heading);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* The symbol before a label that stands in for a title (NoteRowView's
   `Label(text, systemImage:)`), in the line rather than the icon's own
   block. */
.note-card__title .note-card__title-icon {
  display: inline-block;
  margin-inline-end: 6px;
  vertical-align: -2px;
}

.note-card__preview {
  margin: 0;
  font-size: 13px;
  color: var(--app-text-muted);
  line-height: 1.5;
  /* Hard cap at 4 lines — the explicit max-height ensures even pre-line
     content with raw newlines never bleeds past 4 visual rows. */
  max-height: calc(1.5em * 4);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  flex: 0 1 auto;
}

.note-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--app-text-muted);
}

.note-card__badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--app-accent) 14%, transparent);
  color: var(--app-accent);
}

.note-card__badge--warn {
  background: color-mix(in srgb, #ef4444 16%, transparent);
  color: #b91c1c;
}
</style>
